import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Calendar as CalendarIcon, Clock, Car } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";
import PlaceInput from "@/components/PlaceInput";
import { trackEvent } from "@/lib/analytics";

const TIME_SLOTS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    const ampm = h < 12 ? "AM" : "PM";
    const value = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    const label = `${hour12}:${m.toString().padStart(2, "0")} ${ampm}`;
    TIME_SLOTS.push({ value, label });
  }
}

const DURATION_OPTIONS = Array.from({ length: 24 }, (_, i) => (i + 1) * 2);

export default function BookingWidget() {
  const { lang } = useLanguage();
  const isArabic = lang === "ar";
  const navigate = useNavigate();

  const pickupRef = useRef(null);
  const dropoffRef = useRef(null);

  const [bookingMode, setBookingMode] = useState("dropoff");
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [pickupPlace, setPickupPlace] = useState(null);
  const [dropoffPlace, setDropoffPlace] = useState(null);
  const [date, setDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState("4");

  const formattedDate = useMemo(() => {
    return date ? format(date, "yyyy-MM-dd") : "";
  }, [date]);

  const handleViewOptions = () => {
    const pickupValue = pickupRef.current?.getValue?.() || pickup;
    const dropoffValue = dropoffRef.current?.getValue?.() || dropoff;

    const finalPickup = pickupValue.trim();
    const finalDropoff = dropoffValue.trim();

    if (!finalPickup || !date || !time) {
      alert(isArabic ? "يرجى تعبئة جميع الحقول المطلوبة" : "Please fill all required fields");
      return;
    }

    if (bookingMode === "dropoff" && !finalDropoff) {
      alert(isArabic ? "يرجى إدخال وجهة الوصول" : "Please enter the drop-off location");
      return;
    }

    trackEvent("booking_funnel_view_options", {
      source: "hero_widget",
      booking_mode: bookingMode,
      trip_type: bookingMode === "hourly" ? "hourly" : "dropoff",
      has_dropoff: bookingMode === "dropoff" ? "yes" : "no",
      pickup_place_type: pickupPlace?.types?.[0] || "",
      dropoff_place_type: dropoffPlace?.types?.[0] || "",
      pickup_place_id: pickupPlace?.placeId || "",
      dropoff_place_id: dropoffPlace?.placeId || "",
      date: formattedDate,
      time,
      duration: bookingMode === "hourly" ? duration : "",
    });

    const params = new URLSearchParams({
      mode: bookingMode,
      from: finalPickup,
      to: bookingMode === "dropoff" ? finalDropoff : "",
      date: formattedDate,
      time,
      duration: bookingMode === "hourly" ? duration : "",
      pickupPlaceType: pickupPlace?.types?.join(",") || "",
      dropoffPlaceType: dropoffPlace?.types?.join(",") || "",
      pickupPlaceName: pickupPlace?.name || "",
      dropoffPlaceName: dropoffPlace?.name || "",
      pickupPlaceId: pickupPlace?.placeId || "",
      dropoffPlaceId: dropoffPlace?.placeId || "",
      pickupLat: pickupPlace?.lat != null ? String(pickupPlace.lat) : "",
      pickupLng: pickupPlace?.lng != null ? String(pickupPlace.lng) : "",
      dropoffLat: dropoffPlace?.lat != null ? String(dropoffPlace.lat) : "",
      dropoffLng: dropoffPlace?.lng != null ? String(dropoffPlace.lng) : "",
    });

    navigate(`/booking/options?${params.toString()}`);
  };

  return (
    <div
      dir={isArabic ? "rtl" : "ltr"}
      className="w-full max-w-[640px] mx-auto rounded-[34px] border border-white/12 bg-[linear-gradient(180deg,rgba(15,16,20,0.96),rgba(8,8,10,0.94))] shadow-[0_30px_80px_rgba(0,0,0,0.42)] backdrop-blur-xl p-4 md:p-5"
      data-testid="booking-widget"
    >
      <div className="inline-flex w-full rounded-full border border-white/10 bg-white/[0.035] p-1.5 mb-4 md:mb-5">
        <button
          onClick={() => {
            setBookingMode("dropoff");
            trackEvent("booking_mode_change", { mode: "dropoff", source: "hero_widget" });
          }}
          className={`flex-1 px-5 py-3 rounded-full text-sm md:text-[15px] font-medium transition ${
            bookingMode === "dropoff"
              ? "bg-[#C7A239] text-black shadow-[0_10px_24px_rgba(199,162,57,0.25)]"
              : "text-white hover:bg-white/6"
          }`}
        >
          {isArabic ? "توصيل" : "Drop off"}
        </button>

        <button
          onClick={() => {
            setBookingMode("hourly");
            trackEvent("booking_mode_change", { mode: "hourly", source: "hero_widget" });
          }}
          className={`flex-1 px-5 py-3 rounded-full text-sm md:text-[15px] font-medium transition ${
            bookingMode === "hourly"
              ? "bg-[#C7A239] text-black shadow-[0_10px_24px_rgba(199,162,57,0.25)]"
              : "text-white hover:bg-white/6"
          }`}
        >
          {isArabic ? "حجز بالساعات" : "Hourly Booking"}
        </button>
      </div>

      <div className="space-y-4 min-w-0">
        <div className="rounded-[28px] bg-[#101218] border border-white/10 px-4 md:px-5 py-4 min-w-0 overflow-visible relative z-30">
          <div className="space-y-4">
            <div className="relative min-w-0">
              <p className="text-[11px] uppercase tracking-[0.24em] text-gray-500 mb-2 pl-10">
                {isArabic ? "موقع الانطلاق" : "Pick-up location"}
              </p>
              <MapPin className="absolute left-2 top-[34px] w-4 h-4 text-[#C7A239] z-10" />
              <div className="pl-10 min-w-0 overflow-visible relative">
                <PlaceInput
                  ref={pickupRef}
                  value={pickup}
                  onChange={setPickup}
                  onPlaceSelect={setPickupPlace}
                  placeholder={isArabic ? "من أين؟" : "Where do you need to be picked up?"}
                  className="place-input-shell place-input-dark"
                />
              </div>
            </div>

            {bookingMode === "dropoff" && (
              <div className="relative min-w-0">
                <p className="text-[11px] uppercase tracking-[0.24em] text-gray-500 mb-2 pl-10">
                  {isArabic ? "موقع الوصول" : "Drop-off location"}
                </p>
                <MapPin className="absolute left-2 top-[34px] w-4 h-4 text-gray-500 z-10" />
                <div className="pl-10 min-w-0 overflow-visible relative">
                  <PlaceInput
                    ref={dropoffRef}
                    value={dropoff}
                    onChange={setDropoff}
                    onPlaceSelect={setDropoffPlace}
                    placeholder={isArabic ? "إلى أين؟" : "Where are you going?"}
                    className="place-input-shell place-input-dark"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <button className="w-full rounded-[24px] bg-[#101218] border border-white/10 px-4 py-4 text-left hover:border-white/20 transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gray-500 mb-2">
                <CalendarIcon className="w-4 h-4 text-[#C7A239]" />
                <span>{isArabic ? "التاريخ" : "Date"}</span>
              </div>
              <div className="text-white text-[15px] md:text-base leading-6 truncate">
                {date ? format(date, "EEE, MMM d") : isArabic ? "اختر التاريخ" : "Select date"}
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-[#111] border-white/10" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(selectedDate) => {
                setDate(selectedDate);
                setDateOpen(false);
              }}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              className="text-white"
            />
          </PopoverContent>
        </Popover>

        <Select value={time} onValueChange={setTime}>
          <SelectTrigger className="rounded-[24px] bg-[#101218] border border-white/10 px-4 py-4 h-auto hover:border-white/20 focus:ring-[#C7A239]/30 focus:border-[#C7A239]/50 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
            <div className="w-full text-left min-w-0">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gray-500 mb-2">
                <Clock className="w-4 h-4 text-[#C7A239]" />
                <span>{isArabic ? "وقت الانطلاق" : "Pickup time"}</span>
              </div>
              <div className="text-white text-[15px] md:text-base leading-6 truncate">
                <SelectValue placeholder={isArabic ? "اختر الوقت" : "Select time"} />
              </div>
            </div>
          </SelectTrigger>
          <SelectContent className="bg-[#111] border-white/10 max-h-60">
            {TIME_SLOTS.map((slot) => (
              <SelectItem
                key={slot.value}
                value={slot.value}
                className="text-white hover:bg-white/10 focus:bg-white/10 focus:text-white"
              >
                {slot.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {bookingMode === "hourly" && (
          <Select value={duration} onValueChange={setDuration}>
            <SelectTrigger className="rounded-[24px] bg-[#101218] border border-white/10 px-4 py-4 h-auto hover:border-white/20 focus:ring-[#C7A239]/30 focus:border-[#C7A239]/50 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
              <div className="w-full text-left min-w-0">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gray-500 mb-2">
                  <Car className="w-4 h-4 text-[#C7A239]" />
                  <span>{isArabic ? "المدة" : "Duration"}</span>
                </div>
                <div className="text-white text-[15px] md:text-base leading-6 truncate">
                  <SelectValue placeholder={isArabic ? "اختر المدة" : "Select duration"} />
                </div>
              </div>
            </SelectTrigger>
            <SelectContent className="bg-[#111] border-white/10 max-h-60">
              {DURATION_OPTIONS.map((hours) => (
                <SelectItem
                  key={hours}
                  value={String(hours)}
                  className="text-white hover:bg-white/10 focus:bg-white/10 focus:text-white"
                >
                  {hours} {isArabic ? "ساعات" : "hours"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <button
          onClick={handleViewOptions}
          className="w-full h-[72px] rounded-[24px] bg-[#C7A239] hover:bg-[#D4B34A] text-black font-semibold px-6 text-[15px] md:text-[15px] transition-all duration-300 shadow-[0_14px_36px_rgba(199,162,57,0.22)] flex items-center justify-center"
        >
          {isArabic ? "عرض الخيارات" : "View options"}
        </button>
      </div>
    </div>
  );
}
